import { useQuery } from '@tanstack/react-query'
import { Select, Input } from 'antd'
import moment from 'moment'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import MoneyIcon from '@assets/icons/MoneySvg'
import ViewIcon from '@assets/icons/viewSvg'
import Header from '@components/header'
import Layout from '@components/Layout'
import MastersReportDetailModal from '@components/Modal/MastersReportDetailModal'
import VendorPaymentsModal from '@components/Modal/VendorPaymentsModal'
import { useDebounce } from '@hooks/useDebounce'
import { downloadExcel } from '@utils/downloadPdf'
import { fetchMastersTurnoverReport } from '@utils/fetchData'
import { numberWithSpacesIntl } from '@utils/numberWithSpaces'

const MasterReports = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const formattedDate = moment().format('YYYY-MM-DD')

	const [dateFrom, setDateFrom] = useState(moment().startOf("year").format("YYYY-MM-DD"))
	const [dateTo, setDateTo] = useState('')
	const [cardCode, setCardCode] = useState('')
	const [cardName, setCardName] = useState('')

	const debouncedCardCode = useDebounce(cardCode, 500)
	const debouncedCardName = useDebounce(cardName, 500)

	const [loadingExcel, setLoadingExcel] = useState(false)
	const [viewModalOpen, setViewModalOpen] = useState(false)
	const [selectedRowData, setSelectedRowData] = useState(null)
	const [vendorModalOpen, setVendorModalOpen] = useState(false)

	const { data = [], isLoading } = useQuery({
		queryKey: [
			'masters-turnover-report',
			dateFrom,
			dateTo,
			debouncedCardCode,
			debouncedCardName,
		],
		queryFn: fetchMastersTurnoverReport,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	})

	const handleViewClick = (record) => {
		setSelectedRowData(record)
		setViewModalOpen(true)
	}

	const handleVendorClick = (record) => {
		setSelectedRowData(record)
		setVendorModalOpen(true)
	}

	const columns = [
		{
			title: t('cardCode'),
			dataIndex: 'Код БП',
			key: 'Код БП',
		},
		{
			title: t('cardName'),
			dataIndex: 'Наименование БП',
			key: 'Наименование БП',
		},
			{
			title: t('Начальное сальдо'),
			dataIndex: 'Начальное сальдо',
			key: 'Начальное сальдо',
			render: (value) => numberWithSpacesIntl(value || 0),
		},

		{
			title: t('debitTurnover'),
			dataIndex: 'Дебетовый оборот',
			key: 'Дебетовый оборот',
			render: (value) => numberWithSpacesIntl(value || 0),
		},
		{
			title: t('creditTurnover'),
			dataIndex: 'Кредитовый оборот',
			key: 'Кредитовый оборот',
			render: (value) => numberWithSpacesIntl(value || 0),
		},

		{
			title: t('Конечное сальдо'),
			dataIndex: 'Конечное сальдо',
			key: 'Конечное сальдо',
			render: (value) => numberWithSpacesIntl(value || 0),
		},

		{
			title: t('action'),
			key: 'action',
			render: (_, record, index) => (
				index !== data?.length - 1 ? (
					<div className="flex gap-2">
						<button
							onClick={() => handleViewClick(record)}
							className="bg-[#2198c7] text-white w-[35px] h-[30px] rounded-lg flex justify-center items-center"
						>
							<ViewIcon />
						</button>
						<button
							onClick={() => handleVendorClick(record)}
							className="bg-[#099204] text-white w-[35px] h-[30px] rounded-lg flex justify-center items-center"
						>
							<MoneyIcon />
						</button>
					</div>
				) : null
			),
		},
	]

	const handleDownloadExcel = () => {
		downloadExcel({
			url: 'api/reports/masters-turnover-report/export',
			params: {
				dateFrom,
				dateTo,
				cardCode: debouncedCardCode,
				cardName: debouncedCardName,
			},
			fileName: t('mastersTurnoverReport'),
			onLoadingChange: setLoadingExcel,
		})
	}

	return (
		<Layout>
			<Header
				title={'mastersTurnoverReport'}
				columns={columns}
				fdata={data}
				loading={isLoading}
				formattedDate={formattedDate}
				loadingExcel={loadingExcel}
				handleDownloadExcel={handleDownloadExcel}
				isShowExcelBtn
				renderFilters={() => {
					return (
						<div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 items-end gap-4">
							<div className="flex flex-col gap-1">
								<p className="font-nunito">{t('startDate')}</p>
								<Input
									type="date"
									value={dateFrom}
									onChange={(e) => setDateFrom(e.target.value)}
									placeholder={t('startDate')}
									allowClear
								/>
							</div>
							<div className="flex flex-col gap-1">
								<p className="font-nunito">{t('endDate')}</p>
								<Input
									type="date"
									value={dateTo}
									onChange={(e) => setDateTo(e.target.value)}
									placeholder={t('endDate')}
									allowClear
								/>
							</div>
							<div className="flex flex-col gap-1">
								<p className="font-nunito">{t('cardCode')}</p>
								<Input
									value={cardCode}
									onChange={(e) => setCardCode(e.target.value)}
									placeholder={t('cardCode')}
									allowClear
								/>
							</div>
							<div className="flex flex-col gap-1">
								<p className="font-nunito">{t('cardName')}</p>
								<Input
									value={cardName}
									onChange={(e) => setCardName(e.target.value)}
									placeholder={t('cardName')}
									allowClear
								/>
							</div>
						</div>
					)
				}}
			/>
			<MastersReportDetailModal
				visible={viewModalOpen}
				onClose={() => setViewModalOpen(false)}
				cardCode={selectedRowData?.['Код БП']}
				cardName={selectedRowData?.['Наименование БП']}
				initialDateFrom={dateFrom}
				initialDateTo={dateTo}
			/>
			<VendorPaymentsModal
				visible={vendorModalOpen}
				onClose={() => setVendorModalOpen(false)}
				cardCode={selectedRowData?.['Код БП']}
				cardName={selectedRowData?.['Наименование БП']}
				initialDateFrom={dateFrom}
				initialDateTo={dateTo}
			/>
		</Layout>
	)
}

export default MasterReports
